import React, { useState, useEffect } from 'react';
import { doc, updateDoc } from 'firebase/firestore';
import { db } from '../../firebase/config';
import SubscriptionService from '../../services/SubscriptionService';
import './PlanUpgrade.css';

const PlanUpgrade = ({ user, currentSubscription, onUpgradeComplete, onClose }) => {
  const [selectedAccounts, setSelectedAccounts] = useState(currentSubscription?.maxAccounts || 1);
  const [calculatedPrice, setCalculatedPrice] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Pricing calculation (same as original)
  const calculatePrice = (n) => {
    if (n >= 50) return 999.00;
    const price = 999 - (999 - 200) * Math.exp(-0.08 * (n - 1));
    return Math.min(Math.round(price * 100) / 100, 999.00);
  };

  useEffect(() => {
    setCalculatedPrice(calculatePrice(selectedAccounts));
  }, [selectedAccounts]);

  const handleSliderChange = (e) => {
    const value = parseInt(e.target.value);
    setSelectedAccounts(value);
  };

  const handleUpgrade = async () => {
    setLoading(true);
    setError('');

    try {
      const newPlanData = {
        accounts: selectedAccounts,
        price: calculatedPrice,
        isUnlimited: selectedAccounts >= 50,
        planType: selectedAccounts >= 50 ? 'unlimited' : 'standard'
      };

      // For now, update directly in Firebase (replace with Stripe later)
      const userRef = doc(db, 'users', user.uid);
      await updateDoc(userRef, {
        maxAccounts: newPlanData.accounts,
        subscriptionPrice: newPlanData.price,
        isUnlimited: newPlanData.isUnlimited,
        subscriptionType: newPlanData.planType,
        updatedAt: new Date()
      });

      onUpgradeComplete(newPlanData);
    } catch (error) {
      console.error('Error upgrading plan:', error);
      setError('Failed to upgrade plan. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleDowngradeToFree = async () => {
    if (!window.confirm('Downgrade to free plan? You\'ll be limited to 10 videos per month and 1 account.')) {
      return;
    }

    setLoading(true);
    setError('');

    try {
      const userRef = doc(db, 'users', user.uid);
      await updateDoc(userRef, {
        subscriptionType: 'free',
        maxAccounts: 1,
        maxVideosPerMonth: 10,
        videosUsedThisMonth: 0,
        subscriptionPrice: 0,
        isUnlimited: false,
        currentPeriodStart: new Date(),
        // Keep only first account if they have multiple
        twitterUsernames: currentSubscription.twitterUsernames?.slice(0, 1) || [],
        updatedAt: new Date()
      });

      onUpgradeComplete({
        type: 'free',
        maxAccounts: 1,
        maxVideosPerMonth: 10
      });
    } catch (error) {
      console.error('Error downgrading to free:', error);
      setError('Failed to downgrade plan. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const getSliderBackground = () => {
    const percentage = ((selectedAccounts - 1) / 49) * 100;
    return `linear-gradient(to right, #96ccff 0%, #96ccff ${percentage}%, #333333 ${percentage}%, #333333 100%)`;
  };

  const isUpgrade = calculatedPrice > (currentSubscription?.subscriptionPrice || 0);
  const isDowngrade = calculatedPrice < (currentSubscription?.subscriptionPrice || 0);
  const isSamePlan = selectedAccounts === currentSubscription?.maxAccounts;

  const currentPlanInfo = SubscriptionService.getSubscriptionDisplayInfo(currentSubscription);

  return (
    <div className="plan-upgrade-overlay">
      <div className="plan-upgrade-modal">
        <div className="upgrade-header">
          <h2>Change Your Plan</h2>
          <button className="close-btn" onClick={onClose}>×</button>
        </div>

        {error && (
          <div className="upgrade-error">
            <span className="error-icon">⚠️</span>
            <span>{error}</span>
            <button onClick={() => setError('')} className="error-close">×</button>
          </div>
        )}

        <div className="current-plan-info">
          <div className="current-plan-card">
            <div className="plan-status">
              <span className="status-icon">
                {currentSubscription?.subscriptionType === 'free' ? '🆓' : 
                 currentSubscription?.isUnlimited ? '🚀' : '⭐'}
              </span>
              <div className="status-content">
                <h4>Current Plan</h4>
                <p>{currentPlanInfo.title}</p>
                <small>{currentPlanInfo.description}</small>
              </div>
              <div className="current-price">
                ${currentSubscription?.subscriptionPrice || 0}/mo
              </div>
            </div>
          </div>
        </div>

        {currentSubscription?.subscriptionType !== 'free' && (
          <div className="upgrade-section">
            <div className="plan-selector">
              <div className="selector-header">
                <h3>Select New Plan</h3>
                <div className="account-display">
                  <span className="account-count">{selectedAccounts}</span>
                  <span className="account-label">
                    {selectedAccounts >= 50 ? 'Unlimited Accounts' : selectedAccounts === 1 ? 'Account' : 'Accounts'}
                  </span>
                </div>
              </div>

              <div className="slider-container">
                <input
                  type="range"
                  min="1"
                  max="50"
                  value={selectedAccounts}
                  onChange={handleSliderChange}
                  className="account-slider"
                  style={{ background: getSliderBackground() }}
                />
                <div className="slider-labels">
                  <span>1</span>
                  <span>25</span>
                  <span>50+</span>
                </div>
              </div>

              <div className="new-price-display">
                <div className="price-comparison">
                  <div className="new-price">
                    <span className="price-label">New Price</span>
                    <div className="price-amount">
                      <span className="currency">$</span>
                      <span className="price">{calculatedPrice.toFixed(2)}</span>
                      <span className="period">/month</span>
                    </div>
                  </div>
                  
                  {!isSamePlan && (
                    <div className="price-difference">
                      {isUpgrade ? (
                        <span className="upgrade-indicator">
                          +${(calculatedPrice - (currentSubscription?.subscriptionPrice || 0)).toFixed(2)}/mo
                        </span>
                      ) : (
                        <span className="downgrade-indicator">
                          -${((currentSubscription?.subscriptionPrice || 0) - calculatedPrice).toFixed(2)}/mo savings
                        </span>
                      )}
                    </div>
                  )}
                </div>

                {selectedAccounts >= 50 && (
                  <div className="unlimited-badge">
                    <span>🚀 Unlimited Access</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        <div className="upgrade-actions">
          {currentSubscription?.subscriptionType !== 'free' && (
            <button 
              className="downgrade-free-btn"
              onClick={handleDowngradeToFree}
              disabled={loading}
            >
              Downgrade to Free
            </button>
          )}
          
          <div className="primary-actions">
            <button className="cancel-btn" onClick={onClose}>
              Cancel
            </button>
            
            {currentSubscription?.subscriptionType !== 'free' && !isSamePlan && (
              <button 
                className={`upgrade-btn ${isUpgrade ? 'upgrade' : 'downgrade'}`}
                onClick={handleUpgrade}
                disabled={loading}
              >
                {loading ? '...' : isUpgrade ? `Upgrade Plan` : `Downgrade Plan`}
              </button>
            )}
          </div>
        </div>

        <div className="upgrade-notice">
          <div className="notice-content">
            <span className="notice-icon">💡</span>
            <div className="notice-text">
              <p><strong>Plan Changes:</strong></p>
              <ul>
                <li>Upgrades take effect immediately</li>
                <li>Downgrades apply at next billing cycle</li>
                <li>Free plan limits videos to 10/month</li>
                <li>Account limits enforced immediately</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PlanUpgrade; 